// ============================================
// useExport Hook
// Trigger export Excel/PDF from backend API
// Usage: add ?export=xlsx or ?export=pdf to any list endpoint
// ============================================

import { useState, useCallback } from 'react';
import { env } from '../config/env';

export type ExportFormat = 'xlsx' | 'pdf';

interface UseExportOptions {
  /** API path, e.g. '/orders/' */
  endpoint: string;
  /** Extra query params to pass (filters, store_id, etc.) */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** File name prefix, e.g. 'orders' → 'orders_export.xlsx' */
  filePrefix?: string;
}

function buildQueryString(
  params: Record<string, string | number | boolean | undefined | null>,
  format: ExportFormat
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  search.set('export', format);
  return search.toString();
}

export function useExport({ endpoint, params = {}, filePrefix = 'export' }: UseExportOptions) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const triggerExport = useCallback(
    (format: ExportFormat) => {
      setIsExporting(true);
      setExportError(null);
      setProgress(0);

      const token = localStorage.getItem('conai_admin_access_token') || sessionStorage.getItem('conai_admin_access_token');
      const qs = buildQueryString(params, format);
      const base = env.apiBaseUrl.replace(/\/$/, '');
      const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const url = `${base}${path}?${qs}`;

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setProgress(percentComplete);
        } else {
          // Fake progress if content-length is missing
          setProgress((prev) => Math.min(prev + 10, 90));
        }
      };

      xhr.onload = () => {
        setIsExporting(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          setProgress(100);
          const blob = xhr.response as Blob;
          const ext = format === 'xlsx' ? 'xlsx' : 'pdf';
          const timestamp = new Date().toISOString().slice(0, 10);
          const fileName = `${filePrefix}_${timestamp}.${ext}`;

          const objectUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = objectUrl;
          anchor.download = fileName;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          URL.revokeObjectURL(objectUrl);
        } else {
          setExportError(`Export failed: ${xhr.status} ${xhr.statusText}`);
        }
      };

      xhr.onerror = () => {
        setIsExporting(false);
        setExportError('Export failed');
      };

      xhr.send();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, filePrefix, JSON.stringify(params)]
  );

  return { triggerExport, isExporting, exportError, progress };
}
